import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded'
import {
  adminService,
  type AdminPoll,
  type AdminQuestion,
} from '../../services/adminService'
import type { Session } from '../../data/types'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import { useEventStream } from '../../hooks/useEventStream'

export default function EngagementManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [polls, setPolls] = useState<AdminPoll[]>([])
  const [questions, setQuestions] = useState<AdminQuestion[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [pollDialog, setPollDialog] = useState(false)
  const [answering, setAnswering] = useState<AdminQuestion | null>(null)

  const load = () =>
    Promise.all([
      adminService.listPolls(eventId),
      adminService.listQuestions(eventId),
      adminService.listSessions(eventId),
    ])
      .then(([p, q, s]) => {
        setPolls(p)
        setQuestions(q)
        setSessions(s)
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  // Live-refresh when attendees vote or submit/upvote questions: the backend
  // publishes "poll"/"qa" change signals over the event SSE stream, so reload
  // the affected list instead of requiring a manual refresh.
  useEventStream(eventId, (data) => {
    if (data.kind === 'poll' || data.kind === 'qa') {
      load()
    }
  })

  const setStatus = async (poll: AdminPoll, status: string) => {
    try {
      await adminService.updatePoll(eventId, poll.id, { status })
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const removePoll = async (poll: AdminPoll) => {
    if (!(await confirmDelete(`Delete poll "${poll.question}"?`))) return
    try {
      await adminService.deletePoll(eventId, poll.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const moderate = async (q: AdminQuestion, patch: { status?: string; answered?: boolean }) => {
    try {
      await adminService.updateQuestion(eventId, q.id, patch)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const removeQuestion = async (q: AdminQuestion) => {
    if (!(await confirmDelete('Delete this question?'))) return
    try {
      await adminService.deleteQuestion(eventId, q.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Polls
        </Typography>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setPollDialog(true)}>
          New poll
        </Button>
      </Stack>

      {polls.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No polls yet. Create one and open it during a session for live voting.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {polls.map((p) => (
            <Card key={p.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                  {p.question}
                </Typography>
                <Chip
                  size="small"
                  label={p.status}
                  color={p.status === 'open' ? 'success' : 'default'}
                  sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                />
                {p.status !== 'open' && (
                  <Button size="small" onClick={() => setStatus(p, 'open')}>
                    Open
                  </Button>
                )}
                {p.status === 'open' && (
                  <Button size="small" onClick={() => setStatus(p, 'closed')}>
                    Close
                  </Button>
                )}
                <IconButton size="small" color="error" onClick={() => removePoll(p)}>
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack spacing={0.5}>
                {p.options.map((o) => {
                  const pct = p.totalVotes ? Math.round((o.votes / p.totalVotes) * 100) : 0
                  return (
                    <Box key={o.id}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">{o.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pct}% ({o.votes})
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 3 }} />
                    </Box>
                  )
                })}
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                {(p.sessionTitles && p.sessionTitles.length > 0
                  ? p.sessionTitles
                  : ['No sessions assigned']
                ).map((t, i) => (
                  <Chip key={i} size="small" variant="outlined" label={t} sx={{ height: 20 }} />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {p.totalVotes} total votes
              </Typography>
            </Card>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        Q&amp;A moderation
      </Typography>
      {questions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No questions submitted yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {questions.map((q) => (
            <Card key={q.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip size="small" label={`▲ ${q.upvotes}`} sx={{ fontWeight: 700 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">{q.body}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={q.sessionTitle || 'General'}
                      sx={{ height: 20 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {q.authorName || 'Anonymous'} · {q.status}
                    </Typography>
                  </Stack>
                  {q.answer && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: 'success.50',
                        borderLeft: '3px solid',
                        borderColor: 'success.main',
                      }}
                    >
                      <Typography variant="body2">{q.answer}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Answered{q.answeredBy ? ` by ${q.answeredBy}` : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <IconButton
                  size="small"
                  title={q.answered ? 'Edit answer' : 'Answer'}
                  onClick={() => setAnswering(q)}
                >
                  {q.answered ? (
                    <CheckCircleRoundedIcon fontSize="small" color="success" />
                  ) : (
                    <QuestionAnswerRoundedIcon fontSize="small" />
                  )}
                </IconButton>
                {q.status === 'hidden' ? (
                  <IconButton size="small" title="Show" onClick={() => moderate(q, { status: 'approved' })}>
                    <VisibilityRoundedIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton size="small" title="Hide" onClick={() => moderate(q, { status: 'hidden' })}>
                    <VisibilityOffRoundedIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" color="error" onClick={() => removeQuestion(q)}>
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {pollDialog && (
        <PollDialog
          eventId={eventId}
          sessions={sessions}
          onClose={() => setPollDialog(false)}
          onSaved={() => {
            setPollDialog(false)
            load()
          }}
        />
      )}

      {answering && (
        <AnswerDialog
          eventId={eventId}
          question={answering}
          onClose={() => setAnswering(null)}
          onSaved={() => {
            setAnswering(null)
            load()
          }}
        />
      )}
    </Box>
  )
}

function AnswerDialog({
  eventId,
  question,
  onClose,
  onSaved,
}: {
  eventId: string
  question: AdminQuestion
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [answer, setAnswer] = useState(question.answer || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!answer.trim()) {
      notify('An answer is required to mark this question answered', 'error')
      return
    }
    setSaving(true)
    try {
      await adminService.updateQuestion(eventId, question.id, {
        answered: true,
        answerBody: answer.trim(),
      })
      notify('Answer posted')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Answer question</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {question.body}
          </Typography>
          <TextField
            label="Your answer"
            fullWidth
            multiline
            minRows={3}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            helperText="This answer will be shown inline to attendees."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !answer.trim()}>
          {saving ? 'Saving…' : 'Post answer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PollDialog({
  eventId,
  sessions,
  onClose,
  onSaved,
}: {
  eventId: string
  sessions: Session[]
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState<string[]>(['', ''])
  const [status, setStatus] = useState('open')
  const [sessionIds, setSessionIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const titleFor = (id: string) => sessions.find((s) => s.id === id)?.title || id

  const save = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean)
    if (!question.trim() || opts.length < 2) {
      notify('Add a question and at least two options', 'error')
      return
    }
    if (sessionIds.length === 0) {
      notify('Assign the poll to at least one session', 'error')
      return
    }
    setSaving(true)
    try {
      await adminService.createPoll(eventId, {
        question: question.trim(),
        options: opts,
        status,
        sessionIds,
      })
      notify('Poll created')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New poll</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Question" fullWidth value={question} onChange={(e) => setQuestion(e.target.value)} />
          {options.map((o, i) => (
            <TextField
              key={i}
              label={`Option ${i + 1}`}
              fullWidth
              value={o}
              onChange={(e) => setOptions((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
            />
          ))}
          <Button size="small" onClick={() => setOptions((prev) => [...prev, ''])}>
            Add option
          </Button>
          <FormControl fullWidth>
            <InputLabel id="poll-sessions-label">Sessions</InputLabel>
            <Select
              labelId="poll-sessions-label"
              multiple
              value={sessionIds}
              onChange={(e) =>
                setSessionIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                )
              }
              input={<OutlinedInput label="Sessions" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((id) => (
                    <Chip key={id} size="small" label={titleFor(id)} />
                  ))}
                </Box>
              )}
            >
              {sessions.length === 0 && (
                <MenuItem disabled value="">
                  No sessions yet — create a session first
                </MenuItem>
              )}
              {sessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Checkbox checked={sessionIds.includes(s.id)} />
                  <ListItemText primary={s.title} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField select label="Initial status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="draft">Draft (hidden)</MenuItem>
            <MenuItem value="open">Open (accepting votes)</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || sessionIds.length === 0}>
          {saving ? 'Saving…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
